import useAddEditSite from 'hooks/addEdit/useAddEditSite';
import {type Site} from 'client';

import InputActionButtons from './InputActionButtons';
import {handleKeyDown} from 'components/InputMenu/helpers';
import {useInputMenuContext} from 'context/InputMenu';
import useIsMounted from 'hooks/useIsMounted';
import GenericTextField from 'components/Generic/TextField';
import {useTranslation} from 'react-i18next';
import {MABTable} from './Concession/MABTable';
import {useGetPlan} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {Stack, Typography} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {type ChangeEvent, type KeyboardEvent} from 'react';
import {RegionSelector} from './RegionSelector';
import {NumericField} from 'components/Generic/NumericField';
import {dateReader} from 'helpers/UnitsFormatHandler';
import {SiteConcessionForm} from './SiteConcession';
import {useGetIsChilePlan} from 'hooks/useGetIsChilePlan';

const oneMillion = 1000000;
const oneThousand = 1000;

function AddEditSite() {
  const {t} = useTranslation('common');

  const isMounted = useIsMounted();
  const {site, setSite, createUpdateSite, setCreateUpdateSite, isSiteFormValid, siteId} = useAddEditSite(isMounted);
  const {planId} = useParseURL();
  const {isChilePlan, conversionRate} = useGetIsChilePlan();
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {data: plan} = useGetPlan({resourceId: planId});
  const {siteOperationalCostsEnabled, siteHarvestCost: siteHarvestCostEnabled} = useFlags();

  const handleChange = (prop: keyof Site) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(',', '.');
    const numberValue = parseFloat(value);
    if (isNaN(numberValue) || prop === 'siteNumber') {
      site && setSite({...site, [prop]: value});
    } else {
      if (prop === 'startupCost') {
        site && setSite({...site, [prop]: (numberValue * oneMillion) / conversionRate});
      } else if (prop === 'operationalCost') {
        site && setSite({...site, [prop]: (numberValue * oneThousand) / conversionRate});
      } else if (prop === 'harvestCost') {
        site && setSite({...site, [prop]: numberValue / conversionRate});
      } else {
        site && setSite({...site, [prop]: numberValue});
      }
    }
  };

  const onSubmit = (event: KeyboardEvent<HTMLDivElement>) => {
    inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateSite);
  };

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack spacing={2}>
        {/* Site Name */}
        <GenericTextField
          label={t('name')}
          required={true}
          value={site?.name ?? ''}
          onChange={handleChange('name')}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateSite);
          }}
          inputProps={{'data-testid': 'name'}}
        />

        {/* siteNumber */}
        <GenericTextField
          label={t('siteNumber')}
          value={site?.siteNumber ?? ''}
          onChange={handleChange('siteNumber')}
          inputProps={{'data-testid': 'number'}}
        />

        {/* Region Picker */}
        <RegionSelector
          {...{regionId: site?.regionId}}
          setRegionId={(regionId: string) => {
            setSite({...site, regionId});
          }}
        />
        {/* siteMAB */}
        {!isChilePlan && (
          <>
            <Typography color={'text.secondary'} sx={{marginLeft: 1}}>
              {t('mabLevels')}
            </Typography>
            <MABTable
              mabs={site?.mab ?? []}
              minDate={plan ? dateReader(plan.startDate) : undefined}
              maxDate={plan ? dateReader(plan.endDate) : undefined}
              onChange={(mab) => {
                if (!site) {
                  return;
                }
                if (mab.length === 1 && mab[0].limit === undefined && mab[0].fromDate === undefined) {
                  setSite({...site, mab: [{fromDate: plan?.startDate}]});
                  return;
                }
                setSite({...site, mab});
              }}
            />
          </>
        )}

        {siteId && isChilePlan && <SiteConcessionForm siteId={siteId} createUpdateSite={createUpdateSite} />}

        {/* startup cost */}
        <NumericField
          {...{handleChange: handleChange('startupCost'), onKeyPress: onSubmit}}
          value={site?.startupCost ? (site?.startupCost * conversionRate) / oneMillion : undefined}
          fieldName="startupCost"
          helperText="startupCostHelperText"
          endAdornment={t('MNOK')}
        />

        {/* operational cost */}
        {(siteOperationalCostsEnabled || isChilePlan) && (
          <NumericField
            {...{handleChange: handleChange('operationalCost'), onKeyPress: onSubmit}}
            value={site?.operationalCost ? (site?.operationalCost * conversionRate) / oneThousand : undefined}
            fieldName="operationalCost"
            helperText="operationalCostHelperText"
            endAdornment={t('KNOK/month')}
          />
        )}

        {/* harvest cost */}
        {(siteHarvestCostEnabled || isChilePlan) && (
          <NumericField
            {...{handleChange: handleChange('harvestCost'), onKeyPress: onSubmit}}
            value={site?.harvestCost ? site?.harvestCost * conversionRate : undefined}
            fieldName="harvestCost"
            helperText="harvestCostHelperText"
            endAdornment={t('NOK/Kg')}
          />
        )}

        {/* latitude */}
        <NumericField
          handleChange={handleChange('latitude')}
          onKeyPress={onSubmit}
          value={site?.latitude}
          fieldName="latitude"
        />

        {/* longitude */}
        <NumericField
          handleChange={handleChange('longitude')}
          onKeyPress={onSubmit}
          value={site?.longitude}
          fieldName="longitude"
        />

        <InputActionButtons
          disabled={!isSiteFormValid()}
          setCreateUpdateInput={setCreateUpdateSite}
          loading={createUpdateSite}
        />
      </Stack>
    </form>
  );
}

export default AddEditSite;
